import {
    type CSSProperties,
    type FC,
    useCallback,
    useEffect,
    useState,
} from "react";
import { getSyntaxHighlighter } from "../../../../app/Common/Highlight/SyntaxHighlighter";

export type ExampleType = {
    title: string;
    language: string;
    content: string;
};

export const Example: FC<ExampleType & {
    height: number;
}> = ({
    height,
    title,
    language,
    content,
}) => {
    const [ ready, setReady ] = useState(false);
    const [ expandable, setExpandable ] = useState(false);
    const [ expanded, setExpanded ] = useState(true);
    const [ code, setCode ] = useState(content);

    useEffect(() => {
        getSyntaxHighlighter()
            .then(highlighter => {
                setCode(
                    highlighter.codeToHtml(content, {
                        lang : language,
                        theme: "github-dark-dimmed",
                    })
                );
            });
    }, [ content, language ]);

    const determineDifference = useCallback((ref: HTMLPreElement | null) => {
        if(!ref) {
            return;
        }

        const bounding = ref.getBoundingClientRect();

        // Don't hide if there is less than 100px difference
        const difference = bounding.height - 100;

        setExpanded(false);
        setExpandable(difference > height);
        setReady(true);
    }, [ height ]);

    const style: CSSProperties = {};

    if(!ready) {
        style.visibility = "hidden";
    }

    const classes = [ "example-wrapper" ];

    if(expandable) {
        classes.push("expandable");

        if(!expanded) {
            classes.push("hidden");
            style.height = `${height}px`;
        } else {
            classes.push("expanded");
        }
    }

    return (
        <div className={classes.join(" ")} style={style}>
            <div className="example-title">{title}</div>
            <pre
                ref={determineDifference}
                className={`language-${language}`}
                dangerouslySetInnerHTML={{ __html: code }}
            />
            {expandable && (
                expanded ? (
                    <div
                        className="example-collapse"
                        onClick={() => setExpanded(false)}
                    >
                        Collapse
                    </div>
                ) : (
                    <div className="example-expand">
                        <div
                            className="example-expand-button"
                            onClick={() => setExpanded(true)}
                        >
                            Expand
                        </div>
                    </div>
                )
            )}
        </div>
    );
};
